import { FORWARD_DEPLOYED_SPEC } from "./forward-deployed";
import type { DisciplineSpec } from "./types";

/**
 * The set of disciplines known to the runtime. Each consumer (radar,
 * three-axis-aggregator, corpus UI, calibration runner) reads its
 * discipline-specific behavior from this map keyed by slug.
 *
 * To add a new discipline:
 *   1. Create `shared/labs/disciplines/<slug>.ts` exporting a DisciplineSpec.
 *   2. Add it to this registry.
 *   3. Author `docs/labs/seed/framework-<slug>.md` with the hire floor table.
 *   4. Seed `scoring_configs` with `framework:<slug>` pointing at the markdown.
 *
 * No consumer code edits should be required. If a consumer hardcodes the slug,
 * that consumer is a bug — file it and route through the registry instead.
 */
export const DISCIPLINES: Record<string, DisciplineSpec> = {
  [FORWARD_DEPLOYED_SPEC.slug]: FORWARD_DEPLOYED_SPEC,
};

export function getDiscipline(slug: string | null | undefined): DisciplineSpec | undefined {
  if (!slug) return undefined;
  return DISCIPLINES[slug];
}

export function getDisciplineOrThrow(slug: string): DisciplineSpec {
  const spec = DISCIPLINES[slug];
  if (!spec) throw new Error(`Unknown discipline: ${slug}`);
  return spec;
}

export function listDisciplines(): DisciplineSpec[] {
  return Object.values(DISCIPLINES).sort((a, b) => a.slug.localeCompare(b.slug));
}

export function isDisciplineSlug(s: string): boolean {
  return s in DISCIPLINES;
}
